import { Form, Modal, Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAssetTypeManufacturerModelCreateModal } from './hooks/useAssetTypeManufacturerModelCreateModal';

export type AssetTypeManufacturerModelCreateModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export function AssetTypeManufacturerModelCreateModal({
  open,
  onClose,
  onSuccess,
}: AssetTypeManufacturerModelCreateModalProps) {
  const { t } = useTranslation('componentSchema');
  const {
    form,
    submitting,
    loadingOptions,
    loadingModels,
    assetTypeOptions,
    manufacturerOptions,
    assetModelOptions,
    selectedAssetTypeId,
    handleAssetTypeChange,
    handleCancel,
    handleSubmit,
  } = useAssetTypeManufacturerModelCreateModal({ open, onClose, onSuccess });

  return (
    <Modal
      title={t('atmmCreate.title')}
      open={open}
      onCancel={handleCancel}
      onOk={() => void handleSubmit()}
      confirmLoading={submitting}
      okText={t('atmmCreate.submit')}
      cancelText={t('atmmCreate.cancel')}
      destroyOnClose
      width={560}
    >
      <Spin spinning={loadingOptions}>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="assetTypeId"
            label={t('atmmCreate.assetType')}
            rules={[
              {
                required: true,
                message: t('atmmCreate.assetTypeRequired'),
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('atmmCreate.assetTypePlaceholder')}
              options={assetTypeOptions}
              onChange={handleAssetTypeChange}
            />
          </Form.Item>

          <Form.Item
            name="manufacturerId"
            label={t('atmmCreate.manufacturer')}
            rules={[
              {
                required: true,
                message: t('atmmCreate.manufacturerRequired'),
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('atmmCreate.manufacturerPlaceholder')}
              options={manufacturerOptions}
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())
              }
              notFoundContent={
                manufacturerOptions.length === 0 && !loadingOptions
                  ? t('atmmCreate.noManufacturers')
                  : undefined
              }
            />
          </Form.Item>

          <Form.Item
            name="assetModelId"
            label={t('atmmCreate.assetModel')}
            rules={[
              {
                required: true,
                message: t('atmmCreate.assetModelRequired'),
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={
                selectedAssetTypeId
                  ? t('atmmCreate.assetModelPlaceholder')
                  : t('atmmCreate.assetModelDisabledPlaceholder')
              }
              disabled={!selectedAssetTypeId}
              loading={loadingModels}
              options={assetModelOptions}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
}
